export type SellerScValidityRange = {
  scNo: string;
  validFrom: string;
  validUntil: string;
};

type ScValiditySource = Record<string, unknown>;

type DateOnly = {
  iso: string;
  time: number;
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const parseDateOnly = (value: unknown): DateOnly | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = value.getDate();
    return {
      iso: `${year.toString().padStart(4, "0")}-${month
        .toString()
        .padStart(2, "0")}-${day.toString().padStart(2, "0")}`,
      time: Date.UTC(year, month - 1, day),
    };
  }

  const match = String(value || "").trim().match(DATE_ONLY_PATTERN);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const time = Date.UTC(year, month - 1, day);
  const parsed = new Date(time);

  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }

  return { iso: `${match[1]}-${match[2]}-${match[3]}`, time };
};

const firstDate = (source: ScValiditySource, keys: string[]) => {
  for (const key of keys) {
    const parsed = parseDateOnly(source[key]);
    if (parsed) return parsed;
  }
  return null;
};

export const resolveSellerScValidityRange = (
  source: ScValiditySource | null | undefined
): SellerScValidityRange | null => {
  if (!source) return null;

  const validFrom = firstDate(source, [
    "scValidFrom",
    "scValidFromDate",
    "validFrom",
    "validFromDate",
  ]);
  const validUntil = firstDate(source, [
    "scValidUntil",
    "scValidUntilDate",
    "validUntil",
    "validUntilDate",
  ]);
  const extendedUntil = firstDate(source, [
    "scValidExtendedUntil",
    "scExtendedUntilDate",
    "scValidExtendedUntilDate",
    "extendedUntil",
    "extendedUntilDate",
  ]);

  if (!validFrom || (!validUntil && !extendedUntil)) return null;

  const effectiveUntil =
    validUntil && extendedUntil
      ? validUntil.time >= extendedUntil.time
        ? validUntil
        : extendedUntil
      : validUntil || extendedUntil;

  if (!effectiveUntil || effectiveUntil.time < validFrom.time) return null;

  return {
    scNo: String(source.scNo || source.value || "").trim(),
    validFrom: validFrom.iso,
    validUntil: effectiveUntil.iso,
  };
};

export const getShipmentScValidityError = (
  shipmentDate: unknown,
  validity: SellerScValidityRange
): string | null => {
  const shipment = parseDateOnly(shipmentDate);
  const validFrom = parseDateOnly(validity.validFrom);
  const validUntil = parseDateOnly(validity.validUntil);

  if (!shipment || !validFrom || !validUntil) return null;

  if (shipment.time < validFrom.time) {
    return `Shipment Date must be on or after Seller SC Valid From Date (${validFrom.iso})`;
  }

  if (shipment.time > validUntil.time) {
    return `Shipment Date must be on or before Seller SC Valid Until Date (${validUntil.iso})`;
  }

  return null;
};
